import { toast } from 'sonner';
import {
  CHECK_URL_EMBEDDABLE,
  type CheckUrlEmbeddableResult,
  type CheckUrlEmbeddableVariables,
} from '@scio3/graphql-client';
import { registerActionHandler } from '../action-registry';
import { getNested } from '../nested-keys';
import { normalizePreviewUrl } from '../normalize-preview-url';
import { SYNTHETIC_CHECKED_URL, SYNTHETIC_PREVIEW_BLOCKED } from '../synthetic-form-keys';

/**
 * `verifyEmbeddableUrl` — verifies that a user-entered URL can be embedded inside the webplayer
 * iframe (open-api's `checkUrlEmbeddable` → X-Frame-Options + CSP `frame-ancestors`). Mirrors
 * scio1's `checkHeaderUrl` (advancedWebViewModal).
 *
 * Reads the URL from the field the action is attached to (`ctx.field.key`) — generic across
 * manifests: `webPageUrl` (advanced-web-view), `webLink` (Website). Writes the embeddability result
 * to the synthetic `_previewBlocked` form flag (via `ctx.form`) so a `notice` field gated on
 * `showIf: { _previewBlocked: [true] }` can surface a "preview not available" message.
 *
 * Trigger modes (all supported by the config engine):
 *  - `trigger: 'manual'` (button click) — toasts success/failure for user feedback.
 *  - `trigger: 'change'` / `'blur'` / `'mount'` (runOnChange / runOnBlur / runOnMount auto-triggers) —
 *    SILENT; only updates the `_previewBlocked` flag (the notice is the feedback, not a toast). Website
 *    uses `runOnChange` (live while typing) + `runOnMount` (rehydrate the notice on edit-mode reopen).
 *  - `isSaveCheck` (runOnSave) — SILENT; returns `{ ok: false, reason }` to gate save (advanced-web-view).
 *
 * Manifest usage (Website — buttonless, debounced auto-check while typing):
 *   { key: 'webLink', type: 'string', validation: { pattern: '^https?://\\S+$' }, action: {
 *       label: 'Check preview', handler: 'verifyEmbeddableUrl', requires: ['webLink'],
 *       runOnChange: true, runOnMount: true, hideButton: true
 *   } }
 */
registerActionHandler(
  'verifyEmbeddableUrl',
  async ({ values, form, field, apolloClient, t, isSaveCheck, trigger }) => {
    // Silent for every non-manual trigger (auto change/blur + pre-save) — the result surfaces via
    // the `_previewBlocked` flag / save gate, not a per-keystroke toast.
    const quiet = !!isSaveCheck || (trigger != null && trigger !== 'manual');

    const url = ((getNested(values, field?.key ?? 'webPageUrl') as string) ?? '').trim();
    if (!url) {
      if (!quiet) toast.error(t('Please enter a URL first'));
      return;
    }

    // The header-checker requires an absolute http(s) URL (its `new URL()` 400s otherwise), so
    // check the PREVIEW-normalized form — a schemeless warn-only value like `lumas.com` (prod
    // lets it save; devices resolve it) is checked as `https://lumas.com`, same as what the
    // webplayer preview renders. The saved value stays raw; the field's `warnPattern` keeps
    // flagging it as non-standard either way.
    //
    // A value that can't be normalized at all (inner spaces, ftp://, no real hostname) has
    // UNKNOWN embeddability: clear the notice flag and skip the query, but return `ok: false` so
    // a `runOnSave` gate still blocks it. This preserves Advanced Web View's save-gate contract —
    // its manifest pattern admits `ftp://` and embedded-URL strings, which the checker used to
    // reject (error 400 → blocked). Website is unaffected: it declares no `runOnSave`, and its
    // auto change/mount triggers ignore the return value (the amber warnPattern line is the UX).
    const normalizedUrl = normalizePreviewUrl(url);
    if (!normalizedUrl) {
      const reason = t('URL entered does not seem to be a standard valid URL');
      form?.setValue(SYNTHETIC_PREVIEW_BLOCKED, false);
      if (!quiet) toast.error(reason);
      return { ok: false, reason };
    }
    const { data } = await apolloClient.query<
      CheckUrlEmbeddableResult,
      CheckUrlEmbeddableVariables
    >({
      query: CHECK_URL_EMBEDDABLE,
      variables: { url: normalizedUrl },
      fetchPolicy: 'no-cache',
    });

    // Stale-response guard. Auto-triggered checks race while the user types: `example.c` hits a
    // DNS failure the checker waits ~10s on, `example.com` resolves in ~1s — so the BLOCKED
    // verdict for the abandoned intermediate value lands last and overwrites the fresh flag
    // (notice shown beside a working preview). A result only counts if the field still holds the
    // exact value this run checked.
    const liveRaw = form
      ? ((getNested(form.getValues(), field?.key ?? 'webPageUrl') as string) ?? '').trim()
      : url;
    if (liveRaw !== url) return;

    const result = data?.checkUrlEmbeddable;
    const canEmbed = !!result?.canEmbed;
    const reason = result?.reason ?? t('URL cannot be embedded in an iframe');

    // Drive the notice: blocked ⇔ !canEmbed. Synthetic `_`-keys → stripped from the save payload.
    // `_checkedUrl` (the notice's absolute `href`) is written HERE, with the flag, so a discarded
    // stale run can't leave the notice pointing at an abandoned intermediate URL.
    form?.setValue(SYNTHETIC_CHECKED_URL, normalizedUrl);
    form?.setValue(SYNTHETIC_PREVIEW_BLOCKED, !canEmbed);

    if (canEmbed) {
      if (!quiet) toast.success(t('URL can be embedded'));
      return { ok: true };
    }
    if (!quiet) toast.error(reason);
    return { ok: false, reason };
  },
);
